import { PropertyLeadRequest, PropertyLeadRequestKeys } from 'types/PropertyLead';
import { DEFAULT_REGION, Region } from 'types/Region';
import { create } from 'zustand';

interface State {
    currentTabIndex: number;
    setCurrentTabIndex: (tabIndex: number) => void;
    steps: Array<{
        name: string;
        status: string;
    }>;
    changeStepStatus: (move?: 'forward' | 'backward') => void;
    activeModal: string;
    setActiveModal: (modal: string) => void;
    requestPostedModalVisible: boolean;
    setRequestPostedModalVisible: (visible: boolean) => void;
    propertyRequestDetailModalVisible: boolean;
    setPropertyRequestDetailModalVisible: (visible: boolean) => void;
    requestData: PropertyLeadRequest;
    setRequestData: (data: Partial<PropertyLeadRequest>) => void;
    clearRequestData: () => void;
    errors: Record<PropertyLeadRequestKeys, string>;
    setErrors: (errors: Record<PropertyLeadRequestKeys, string>) => void;
    clearErrors: (field: PropertyLeadRequestKeys) => void;
    region: Region;
    setRegion: (region: Region) => void;
    usePredefinedLocations: boolean;
    setUsePredefinedLocations: (use: boolean) => void;
}

const useAddRequestStore = create<State>((set, get) => ({
    currentTabIndex: 0,
    setCurrentTabIndex: (tabIndex: number) => set({ currentTabIndex: tabIndex }),
    activeModal: '',
    setActiveModal: (modal) => set({ activeModal: modal }),
    requestPostedModalVisible: false,
    setRequestPostedModalVisible: (visible) => set({ requestPostedModalVisible: visible }),
    propertyRequestDetailModalVisible: false,
    setPropertyRequestDetailModalVisible: (visible) => set({ propertyRequestDetailModalVisible: visible }),
    requestData: {} as PropertyLeadRequest,
    setRequestData: (data: Partial<PropertyLeadRequest>) =>
        set((state) => ({
            requestData: {
                ...state.requestData,
                ...data,
            },
        })),
    clearRequestData: () =>
        set({
            requestData: {} as PropertyLeadRequest,
            region: DEFAULT_REGION,
            currentTabIndex: 0,
            usePredefinedLocations: false,
            steps: get().steps.map((step, index) => ({ ...step, status: index === 0 ? 'current' : 'upcoming' })),
        }),
    steps: [
        { name: 'Request Overview', status: 'current' },
        { name: 'Location Details', status: 'upcoming' },
        { name: 'Property Details', status: 'upcoming' },
        { name: 'Budget & Timeline', status: 'upcoming' },
        { name: 'Additional Preferences', status: 'upcoming' },
        { name: 'Contact Information', status: 'upcoming' },
        { name: 'Final Preview', status: 'upcoming' },
    ],
    changeStepStatus: (move = 'forward') => {
        set((state) => {
            const { currentTabIndex, steps } = state;

            const newTabIndex = move === 'forward' ? Math.min(currentTabIndex + 1, steps.length - 1) : Math.max(currentTabIndex - 1, 0);

            const newSteps = steps.map((step, idx) => {
                if (idx < newTabIndex) return { ...step, status: 'complete' };
                if (idx === newTabIndex) return { ...step, status: 'current' };
                return { ...step, status: 'upcoming' };
            });

            set({ errors: {} as Record<PropertyLeadRequestKeys, string> });

            return {
                currentTabIndex: newTabIndex,
                steps: newSteps,
            };
        });
    },
    errors: {} as Record<PropertyLeadRequestKeys, string>,
    setErrors: (errors: Record<PropertyLeadRequestKeys, string>) =>
        set((state) => ({
            errors: { ...state.errors, ...errors },
        })),
    clearErrors: (field: PropertyLeadRequestKeys) =>
        set((state) => {
            const newErrors = { ...state.errors };
            delete newErrors[field];
            return { errors: newErrors };
        }),
    region: DEFAULT_REGION,
    setRegion: (region: Region) => set({ region }),
    usePredefinedLocations: false,
    setUsePredefinedLocations: (use: boolean) => set({ usePredefinedLocations: use }),
}));

export default useAddRequestStore;
